<template>
    <div>
        <Notification class="is-info" :centered="false">
            <p>{{ translate("predefined_notification.enable.hint") }}</p>
            <p v-if="narrowsPermission" class="mt-2">{{ translate("predefined_notification.enable.narrows_hint") }}</p>
        </Notification>
        <div class="field">
            <label class="label">{{ translate("predefined_notification.enable.deadline_label") }}</label>
            <div class="control">
                <NativeDateTimePicker v-model="until" :class="{ 'is-danger': !!error }" :min="min" :max="max" />
            </div>
            <p v-if="error" class="help is-danger">{{ error }}</p>
        </div>
        <div class="buttons is-right">
            <LoadingButton :is-loading="saving" class="button is-primary" @click="enable">
                <span class="icon">
                    <i class="fa fa-check" />
                </span>
                <span>{{ translate("predefined_notification.enable.button") }}</span>
            </LoadingButton>
        </div>
    </div>
</template>

<script setup lang="ts">
    import { computed, onMounted, ref, watch } from "vue";
    import Notification from "../../Common/Notification.vue";
    import NativeDateTimePicker from "../../Common/NativeDateTimePicker.vue";
    import LoadingButton from "../../Common/LoadingButton.vue";
    import { translate } from "../../Common/translations";
    import { handleError } from "../../Common/errorHandler";
    import { convertFromTimezone, convertToTimezone, getCurrentDateTimeInTimezone } from "../../Common/dateTimeUtils";
    import registrationStagesApi from "../../Api/registrationStages";
    import type { SingleCompetitorCategoriesNotification } from "../../Api/predefinedNotifications";

    const props = defineProps<{
        data: SingleCompetitorCategoriesNotification;
        eventId: number;
        eventTimezone: string;
    }>();

    const emit = defineEmits<{
        resolved: [];
    }>();

    const INPUT_FORMAT = "yyyy-MM-dd'T'HH:mm";
    const DATETIME_PATTERN = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}.*$/;

    const until = ref("");
    const error = ref("");
    const saving = ref(false);
    const narrowsPermission = ref(false);

    // UTC, now plus the minimum margin (from the server clock).
    const earliest = computed(() => props.data.deadline?.earliest ?? null);
    const min = computed(() =>
        earliest.value
            ? convertToTimezone(earliest.value, props.eventTimezone, INPUT_FORMAT)
            : getCurrentDateTimeInTimezone(props.eventTimezone).toFormat(INPUT_FORMAT),
    );
    // UTC, the event start date.
    const latest = computed(() => props.data.deadline?.latest ?? null);
    const max = computed(() => (latest.value ? convertToTimezone(latest.value, props.eventTimezone, INPUT_FORMAT) : undefined));

    watch(until, () => {
        error.value = "";
    });

    onMounted(() => {
        // Only needed for the hint: enabling narrows the permission when all registrations are editable after the due date.
        registrationStagesApi
            .getOptions(props.eventId)
            .then(options => {
                narrowsPermission.value =
                    options.allowEditRegistrationsAfterDueDate && !options.allowChangesOnlyInSingleCompetitorCategories;
            })
            .catch(handleError);
    });

    function enable() {
        if (!until.value || !DATETIME_PATTERN.test(until.value)) {
            error.value = translate("flash_messages.invalid_datetime");
            return;
        }
        const utcUntil = convertFromTimezone(until.value, props.eventTimezone);
        if (utcUntil < (earliest.value ?? "") || (latest.value && utcUntil > latest.value)) {
            error.value = translate("predefined_notification.enable.date_out_of_range");
            return;
        }

        saving.value = true;
        registrationStagesApi
            .saveRegistrationStagesOptions({
                eventId: props.eventId,
                allowEditRegistrationsAfterDueDate: true,
                allowEditRegistrationsAfterDueDateUntil: utcUntil,
                allowChangesOnlyInSingleCompetitorCategories: true,
            })
            .then(() => {
                emit("resolved");
            })
            .catch(handleError)
            .finally(() => {
                saving.value = false;
            });
    }
</script>
